import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { ButtonGroup } from "../../../Buttons/ButtonGroup";
import { Alert } from "../../../Alerts/Alert";
import { Input } from "../../Input";
import { Select } from "../../Select";
import { useCreateSetupIntent } from "../../../../api/hooks";
import { useQueryClient } from "react-query";
import { QUERY_KEYS } from "../../../../api/hooks/constants";
import { STRIPE_ICON } from "../../../../assets/icons";

const BILLING_COUNTRIES = [
  { id: "AT", name: "Austria" },
  { id: "AU", name: "Australia" },
  { id: "BE", name: "Belgium" },
  { id: "BR", name: "Brazil" },
  { id: "CA", name: "Canada" },
  { id: "CH", name: "Switzerland" },
  { id: "DE", name: "Germany" },
  { id: "DK", name: "Denmark" },
  { id: "ES", name: "Spain" },
  { id: "FI", name: "Finland" },
  { id: "FR", name: "France" },
  { id: "GB", name: "United Kingdom" },
  { id: "HK", name: "Hong Kong" },
  { id: "IE", name: "Ireland" },
  { id: "IT", name: "Italy" },
  { id: "JP", name: "Japan" },
  { id: "MX", name: "Mexico" },
  { id: "NL", name: "Netherlands" },
  { id: "NO", name: "Norway" },
  { id: "NZ", name: "New Zealand" },
  { id: "SE", name: "Sweden" },
  { id: "SG", name: "Singapore" },
  { id: "US", name: "United States" },
  { id: "AE", name: "United Arab Emirates" },
];

export function SavePaymentForm({ open, setOpen }) {
  const { handleSubmit, control } = useForm({
    defaultValues: {
      name: "",
      country: BILLING_COUNTRIES[4],
    },
    mode: "onSubmit",
  });

  const [savePaymentError, setSavePaymentError] = useState("");
  const [isSavePaymentLoading, setIsSavePaymentLoading] = useState(false);

  const { mutateAsync: createSetupIntent } = useCreateSetupIntent();
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const isLoading = isSavePaymentLoading;

  const onSubmit = async (data) => {
    try {
      setIsSavePaymentLoading(true);

      // Step 1: create setup intent
      const res = await createSetupIntent();
      const clientSecret = res.clientSecret;

      // Step 2: confirm card with Stripe
      const cardElement = elements.getElement(CardElement);
      const stripeRes = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: data.name,
            address: {
              country: data.country.id,
            },
          },
        },
      });

      setIsSavePaymentLoading(false);

      if (stripeRes.error) {
        setSavePaymentError(stripeRes.error.message);
      } else {
        toast.success("Payment method successfully saved");

        queryClient.invalidateQueries([QUERY_KEYS.LIST_PAYMENT_METHODS]);

        setOpen(false);
      }
    } catch (err) {
      setSavePaymentError(err.message);
      setIsSavePaymentLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md">
      {savePaymentError && (
        <div className="pb-4">
          <Alert code={savePaymentError} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Cardholder Name */}
        <Input
          label="Cardholder Name"
          name="name"
          control={control}
          placeholder="Jane Doe"
          rules={{ required: "Cardholder name is required" }}
        />

        {/* Billing Country */}
        <div className="mt-4">
          <Select
            label="Billing Country"
            options={BILLING_COUNTRIES}
            control={control}
            name="country"
            inputSize="xl"
            rules={{ required: "Billing country is required" }}
          />
        </div>

        {/* Card Information */}
        <div className="mt-4">
          <Controller
            name="card"
            control={control}
            rules={{ required: "Card information is required" }}
            render={({ field, fieldState }) => (
              <div>
                <label className="block text-sm font-light text-gray-900 mb-1">
                  Card Information
                </label>
                <div
                  className={`rounded-md ring-1 ring-inset focus-within:ring-2 px-3 py-3 bg-white ${
                    fieldState.error
                      ? "ring-red-300 focus:ring-red-500"
                      : "ring-gray-200 focus-within:ring-blue"
                  }`}
                >
                  <CardElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#111827",
                          fontFamily: "inherit",
                          "::placeholder": { color: "#9ca3af" },
                          height: "48px",
                          padding: "12px 0",
                        },
                        invalid: { color: "#b91c1c" },
                      },
                      hidePostalCode: false, // collect ZIP/Postal inline
                    }}
                    onChange={(e) => field.onChange(e)}
                  />
                </div>
                {/* Stripe trust indicator */}
                <div className="mt-2 flex justify-end w-full">
                    {STRIPE_ICON}
                </div>
                {fieldState.error && (
                  <p className="mt-1 text-sm text-red-600">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* Buttons */}
        <div className="mt-6">
          <ButtonGroup
            primaryButtonText="Save"
            primaryButtonOnClick={handleSubmit(onSubmit)}
            secondaryButtonText="Cancel"
            secondaryButtonOnClick={() => setOpen(false)}
            isLoading={isLoading}
          />
        </div>
      </form>
    </div>
  );
}
